import type { Order } from '@brighterly/lib-core-types/orders/interfaces'
import type { SuccessMessage } from '@solidgate/vue-sdk'
import type { CheckoutStore } from './interfaces'
import type { ScopeMode } from '@shared/lib/scope'

import { AppRoute } from '@shared/config'
import { resolveOrderId } from '@shared/lib/routing'
import { beginEntry, createScopedEntry, failEntry, settleEntry } from '@shared/lib/scope'

import { getOrder } from './active-order'
import { createInitialState } from './state'

const getDetailsUrl = (state: CheckoutStore, solidOrderId: string | null): string | null => {
  if (!solidOrderId) return null

  const orderId = getOrder(state)?.id || null
  if (!orderId) return null

  const url = resolveOrderId(AppRoute.Order, orderId)
  if (!url) return null

  return `${url}?solid_order_id=${solidOrderId}`
}

export const actions = {
  resetState (this: CheckoutStore): void {
    Object.assign(this, createInitialState())
  },
  setLoading (this: CheckoutStore, payload: CheckoutStore['loading']): void {
    this.loading = payload
  },
  setPaymentLocked (this: CheckoutStore, payload: CheckoutStore['paymentLocked']): void {
    this.paymentLocked = payload
  },
  setBlockPromoCode (this: CheckoutStore, payload: CheckoutStore['blockPromoCode']): void {
    this.blockPromoCode = payload
  },
  setAppliesForPromocode (this: CheckoutStore, payload: CheckoutStore['appliesForPromocode']): void {
    this.appliesForPromocode = payload
  },
  setFurtherPayments (this: CheckoutStore, payload: CheckoutStore['furtherPayments']): void {
    this.furtherPayments = payload
  },
  setActiveOrderId (this: CheckoutStore, payload: CheckoutStore['activeOrderId']): void {
    this.activeOrderId = payload
  },
  beginOrder (this: CheckoutStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.orderById, id, mode)
  },
  settleOrder (this: CheckoutStore, id: number, requestId: number, data: Order | null): void {
    settleEntry(this.orderById, id, requestId, data)
  },
  failOrder (this: CheckoutStore, id: number, requestId: number): void {
    failEntry(this.orderById, id, requestId)
  },
  patchOrder (this: CheckoutStore, id: number, order: Order | null): void {
    const entry = this.orderById[id] ?? createScopedEntry<Order>()
    this.orderById[id] = entry
    entry.data = order
  },
  beginUpsellOrder (this: CheckoutStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.upsellOrderById, id, mode)
  },
  settleUpsellOrder (this: CheckoutStore, id: number, requestId: number, data: Order | null): void {
    settleEntry(this.upsellOrderById, id, requestId, data)
  },
  failUpsellOrder (this: CheckoutStore, id: number, requestId: number): void {
    failEntry(this.upsellOrderById, id, requestId)
  },
  patchUpsellOrder (this: CheckoutStore, id: number, order: Order | null): void {
    const entry = this.upsellOrderById[id] ?? createScopedEntry<Order>()
    this.upsellOrderById[id] = entry
    entry.data = order
  },
  setPaymentData (this: CheckoutStore, payload: CheckoutStore['paymentData']): void {
    this.paymentData = payload
  },
  setStripeClientSecret (this: CheckoutStore, payload: CheckoutStore['stripeClientSecret']): void {
    this.stripeClientSecret = payload
  },
  setPaypalScriptUrl (this: CheckoutStore, payload: CheckoutStore['paypalScriptUrl']): void {
    this.paypalScriptUrl = payload
  },
  setModalFurtherPayments (this: CheckoutStore, payload: CheckoutStore['modalFurtherPayments']): void {
    this.modalFurtherPayments = payload
  },
  setPaypalScriptReady (this: CheckoutStore, payload: CheckoutStore['paypalScriptReady']): void {
    this.paypalScriptReady = payload
  },
  getPayPalDetailsUrl (this: CheckoutStore, event: any): string | null {
    const solidOrderId = event?.detail?.data?.order?.order_id || null
    return getDetailsUrl(this, solidOrderId)
  },
  getSolidDetailsUrl (this: CheckoutStore, event: SuccessMessage): string | null {
    const solidOrderId = event?.order?.order_id || null
    return getDetailsUrl(this, solidOrderId)
  },
  setPaymentError (this: CheckoutStore, payload: CheckoutStore['paymentError']): void {
    this.paymentError = payload
  },
  setNotifyType (this: CheckoutStore, payload: CheckoutStore['notifyType']): void {
    this.notifyType = payload
  },
  setModalPaymentFailed (this: CheckoutStore, payload: CheckoutStore['modalPaymentFailed']): void {
    this.modalPaymentFailed = payload
  },
  setBillingCountry (this: CheckoutStore, payload: CheckoutStore['billingCountry']): void {
    this.billingCountry = payload
  },
  setBillingZipCode (this: CheckoutStore, payload: CheckoutStore['billingZipCode']): void {
    this.billingZipCode = payload
  },
  setBillingZipStatus (this: CheckoutStore, payload: CheckoutStore['billingZipStatus']): void {
    this.billingZipStatus = payload
  },
  setBillingCountryState (this: CheckoutStore, payload: CheckoutStore['billingCountryState']): void {
    this.billingCountryState = payload
  },
  setBillingConfirmed (this: CheckoutStore, payload: CheckoutStore['billingConfirmed']): void {
    this.billingConfirmed = payload
  },
  setModalFormSolidgate (this: CheckoutStore, payload: CheckoutStore['modalFormSolidgate']): void {
    this.modalFormSolidgate = payload
  },
  setModalCurrentMethodPayment (this: CheckoutStore, payload: CheckoutStore['modalCurrentMethodPayment']): void {
    this.modalCurrentMethodPayment = payload
  },
}
